import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { REACT_APP_API_URL } from "config/api-config";
import { 
  MessageCircle, ArrowRight, Clipboard, CheckCircle2, 
  HelpCircle, ExternalLink, RefreshCw 
} from "lucide-react";
import { sync_telegram } from "services/api-service"; // <-- Import the new service
export default function TelegramSetup({ onClose, onUpdateSuccess }) {
  const [chatId, setChatId] = useState("");
  const [deviceName, setDeviceName] = useState(""); // <-- NEW STATE
  const [submitting, setSubmitting] = useState(false);
  
// Replace your existing steps array in TelegramSetup.jsx with this:
const steps = [
  {
    number: "1",
    title: "Open our Global Security Bot",
    desc: (
      <span>
        Click the launch button below, or manually open Telegram, search for 
        <strong style={{ color: "#2B3674", marginLeft: "4px" }}>@CamsecAI_bot</strong>, and select it.
      </span>
    ),
    action: (
      <a 
        href="https://t.me/CamsecAI_bot?start=connect" 
        target="_blank" 
        rel="noreferrer"
        style={{
          display: "inline-flex", alignItems: "center", gap: "8px",
          backgroundColor: "#0088cc", color: "white", padding: "10px 18px",
          borderRadius: "10px", fontSize: "14px", fontWeight: "600",
          textDecoration: "none", marginTop: "10px"
        }}
      >
        Launch Telegram Bot <ExternalLink size={14} />
      </a>
    )
  },
  {
    number: "2",
    title: "Get your Secure Chat ID",
    desc: "Tap the 'Start' button inside Telegram. The bot will instantly text you back your unique Chat ID.",
  },
  {
    number: "3",
    title: "Paste & Name the Device",
    desc: "Paste the Chat ID below and give this device a name (e.g., 'Amelia's Phone').",
    action: (
      <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px", maxWidth: "400px" }}>
        {/* NEW NAME INPUT */}
        <input 
          type="text" 
          placeholder="User / Device Name (e.g. Amelia)"
          value={deviceName}
          onChange={(e) => setDeviceName(e.target.value)}
          style={{
            padding: "10px 14px", borderRadius: "10px", border: "1px solid #E2E8F0",
            fontSize: "14px", outline: "none", color: "#2B3674", fontWeight: "600"
          }}
        />
        {/* EXISTING ID INPUT */}
        <input 
          type="text" 
          placeholder="Chat ID (e.g. 1765917481)"
          value={chatId}
          onChange={(e) => setChatId(e.target.value.replace(/\D/g, ""))}
          style={{
            padding: "10px 14px", borderRadius: "10px", border: "1px solid #E2E8F0",
            fontSize: "14px", outline: "none", color: "#2B3674", fontWeight: "600"
          }}
        />
      </div>
    )
  }
];

const handleLinkProfile = async () => {
  if (!chatId) {
    toast.error("Please enter a valid Telegram Chat ID");
    return;
  }

  setSubmitting(true);
  try {
    // 🔥 Replaced raw axios call with the clean service call
    await sync_telegram({ 
      telegram_chat_id: chatId,
      device_name: deviceName || `Device ${chatId.substring(0,4)}`
    });
    
    toast.success("Telegram connected successfully!");
    if (onUpdateSuccess) onUpdateSuccess();
    if (onClose) onClose();
  } catch (error) {
    toast.error(error.response?.data?.detail || "Failed to link profile");
  } finally {
    setSubmitting(false);
  }
};

  // ... (Keep the rest of the return/render block exactly the same) ...
  return (
    <div style={{ backgroundColor: "#FFFFFF", padding: "30px", borderRadius: "20px", boxShadow: "0px 4px 25px rgba(0,0,0,0.05)" }}>
      
      {/* HEADER BLOCK */}
      <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "25px", borderBottom: "1px solid #F4F7FE", paddingBottom: "20px" }}>
        <div style={{ width: "56px", height: "56px", borderRadius: "14px", backgroundColor: "#e6f5ff", display: "flex", alignItems: "center", justifyBackground: "center", justifyContent: "center" }}>
          <MessageCircle size={28} color="#0088cc" />
        </div>
        <div>
          <h3 style={{ margin: 0, color: "#2B3674", fontSize: "20px", fontWeight: "700" }}>Link Telegram Alerts</h3>
          <p style={{ margin: 0, color: "#A3AED0", fontSize: "14px" }}>Follow these simple configuration instructions to bind your device.</p>
        </div>
      </div>

      {/* STEP LOGIC BUILDER */}
      <div style={{ display: "flex", flexDirection: "column", gap: "24px", marginBottom: "30px" }}>
        {steps.map((step, i) => (
          <div key={i} style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>
            <div style={{ 
              minWidth: "32px", height: "32px", borderRadius: "50%", 
              backgroundColor: "#F4F7FE", color: "#4318FF", 
              display: "flex", alignItems: "center", justifyContent: "center", 
              fontWeight: "700", fontSize: "14px" 
            }}>
              {step.number}
            </div>
            <div style={{ flexGrow: 1 }}>
              <h4 style={{ margin: "0 0 4px 0", color: "#2B3674", fontSize: "15px", fontWeight: "700" }}>{step.title}</h4>
              <p style={{ margin: 0, color: "#A3AED0", fontSize: "13px", lineHeight: "1.5" }}>{step.desc}</p>
              {step.action && step.action}
            </div>
          </div>
        ))}
      </div>

      {/* FOOTER VERIFICATION CTAS */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", borderTop: "1px solid #F4F7FE", paddingTop: "20px" }}>
        {onClose && (
          <button 
            onClick={onClose}
            style={{ padding: "12px 20px", backgroundColor: "#F4F7FE", color: "#A3AED0", border: "none", borderRadius: "12px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}
          >
            Cancel
          </button>
        )}
        <button 
          onClick={handleLinkProfile}
          disabled={submitting || !chatId}
          style={{ 
            padding: "12px 24px", 
            backgroundColor: (!chatId || submitting) ? "#E2E8F0" : "#4318FF", 
            color: (!chatId || submitting) ? "#A3AED0" : "white", 
            border: "none", borderRadius: "12px", fontSize: "14px", 
            fontWeight: "600", cursor: (!chatId || submitting) ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: "8px" 
          }}
        >
          {submitting ? <RefreshCw size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
          Save & Verify Connection
        </button>
      </div>

    </div>
  );
}
